import {
  WASH_CUSTOMER_SEARCH_MIN_LENGTH,
  type WashActivationSource,
  type WashActivationStatus,
  type WashAppointmentStatus,
  type WashCreditStatus,
  type WashCycleStatus,
  type WashCycleTrigger,
  type WashProgramKind,
  type WashWorkOrderStatus,
} from "@station/contracts";
import { keepPreviousData, useQueries, useQuery, useQueryClient } from "@tanstack/react-query";
import { useCallback } from "react";
import { api } from "../../lib/api.js";
import { queryKeys } from "../../lib/query.js";
import { useFeatures, usePermissions } from "../../lib/session.js";
import { compact } from "../market/queries.js";
import type { CustomerSearchSource } from "../privacy/customer-picker.js";

/**
 * Yıkama ekranlarının sorguları (Prompt 18 web). Tanımlar (ayar, program) KİRACI; ünite, döngü, aktivasyon, kredi,
 * kimyasal, randevu, iş emri, sıra ve raporlar İSTASYON düzeyindedir (sorgu anahtarında istasyon kimliği vardır).
 *
 * İzinler: okuma `wash.read`; ünite ayarı / program / reçete / kredi düzeltmesi `wash.manage`; kiracı ayarı KİRACI kapsamlı
 * `wash.manage` (sunucu `GET /wash/settings` → `canEdit`); başlatma, elle döngü, randevu, iş emri, fotoğraf `wash.operate`;
 * raporlar `wash.report.read`. Modül `WASH` bayrağına bağlıdır: kapalıysa ekranlar istek atmaz. Randevu / iş emri
 * pencerelerinin fiyatı (`GET /wash/programs?stationId` → `currentPrice`), personeli (`/wash/staff`) ve müşteri araması
 * (`/wash/customers`) yıkama uçlarındandır — yıkama görevlisi (`WASH_ATTENDANT`) fiyat / vardiya / müşteri izni taşımaz.
 *
 * **Tazeleme:** `wash.*` olayları (istasyon odası) modülün tüm önbelleğini geçersiz kılar (`lib/realtime.ts`); mutasyonlar
 * `useRefreshWash` ile `["wash"]`'ı tazeler.
 */
export interface WashPermissions {
  enabled: boolean;
  canRead: boolean;
  canManage: boolean;
  canOperate: boolean;
  canReadReports: boolean;
  canUpload: boolean;
  /**
   * Müşteri seçimi (randevu / iş emri): `GET /wash/customers` — `wash.operate` + `FLEET_LOYALTY` (müşteri kaydı yoksa
   * seçici çizilmez; istasyonda kapalıysa sunucu boş liste döner). `customers.read` GEREKMEZ.
   */
  canSearchCustomers: boolean;
  /** Program ürünü / kimyasal seçimi (`GET /products`). */
  canListProducts: boolean;
  /** Kimyasal ekranından market mal kabul / sayım / stok ekranlarına bağlantı. */
  canOpenMarket: boolean;
  canReadAccounting: boolean;
}

export function useWashPermissions(): WashPermissions {
  const permissions = usePermissions();
  const features = useFeatures();
  const canOperate = permissions.has("wash.operate");
  return {
    enabled: features.has("WASH"),
    canRead: permissions.has("wash.read"),
    canManage: permissions.has("wash.manage"),
    canOperate,
    canReadReports: permissions.has("wash.report.read"),
    canUpload: permissions.has("files.upload"),
    canSearchCustomers: canOperate && features.has("FLEET_LOYALTY"),
    canListProducts: permissions.has("catalog.product.read"),
    canOpenMarket: permissions.has("market.read") && features.has("MARKET"),
    canReadAccounting: permissions.has("accounting.entry.read"),
  };
}

export function useRefreshWash(): () => Promise<void> {
  const queryClient = useQueryClient();
  return useCallback(async () => {
    await queryClient.invalidateQueries({ queryKey: queryKeys.wash() });
  }, [queryClient]);
}

// ── İstasyonlar ──────────────────────────────────────────────────────────────
export interface WashStationOption {
  id: string;
  code: string;
  name: string;
  companyId: string;
  /** İstasyonda MARKET iş birimi etkin mi (kimyasal stoğu tutulur mu). */
  hasMarket: boolean;
}

/** Yıkama iş birimi ETKİN istasyonlar (kiracı geneli; kapsam süzgeci çağıranda). */
export function useWashStations(enabled = true) {
  return useQuery({
    queryKey: queryKeys.wash("stations"),
    enabled,
    staleTime: 300_000,
    queryFn: async (): Promise<WashStationOption[]> =>
      (await api.GET("/stations", { params: { query: { limit: 200 } } }))
        .data!.items.filter((station) =>
          station.businessUnits.some((unit) => unit.unitType === "WASH" && unit.isActive),
        )
        .map((station) => ({
          id: station.id,
          code: station.code,
          name: station.name,
          companyId: station.companyId,
          hasMarket: station.businessUnits.some((unit) => unit.unitType === "MARKET" && unit.isActive),
        })),
  });
}

// ── Tanımlar ─────────────────────────────────────────────────────────────────
export function useWashSettings(enabled = true) {
  return useQuery({
    queryKey: queryKeys.wash("settings"),
    enabled,
    queryFn: async () => (await api.GET("/wash/settings")).data!,
  });
}

/**
 * Tüm yıkama istasyonlarının üniteleri (program ekranında "hangi ünitelerde" sütunu): istasyon başına bir istek —
 * yıkama istasyonu sayısı küçüktür. Sonuç ünite kimliği → "istasyon kodu · ünite adı".
 */
export function useAllWashUnitNames(stations: readonly WashStationOption[], enabled = true) {
  return useQueries({
    queries: stations.map((station) => ({
      queryKey: queryKeys.wash("units", station.id),
      enabled,
      queryFn: async () =>
        (await api.GET("/wash/units", { params: { query: { stationId: station.id } } })).data!.items,
    })),
    combine: (results) => {
      const names = new Map<string, string>();
      results.forEach((result, index) => {
        for (const unit of result.data ?? []) names.set(unit.id, `${stations[index]!.code} · ${unit.name}`);
      });
      return names;
    },
  });
}

export function useWashUnits(stationId: string | null) {
  return useQuery({
    queryKey: queryKeys.wash("units", stationId),
    enabled: stationId !== null,
    // Canlı pano: olay kaçarsa (oda aboneliği kopması) yine de dakikada bir tazelenir.
    refetchInterval: 60_000,
    queryFn: async () =>
      (await api.GET("/wash/units", { params: { query: { stationId: stationId! } } })).data!.items,
  });
}

/**
 * Programlar (kiracı kataloğu): tüm sayfalar çekilir — katalog küçüktür ve seçicilerde sayfalama yoktur. `stationId`
 * verilirse her programın o istasyondaki GÜNCEL fiyatı (`currentPrice`, kuruş) gelir — `wash.read` yeterli (tahmin içindir).
 */
export function useWashPrograms(
  filters: { kind?: WashProgramKind | undefined; stationId?: string | undefined } = {},
  enabled = true,
) {
  const query = compact({ kind: filters.kind, stationId: filters.stationId, limit: 200 });
  return useQuery({
    queryKey: queryKeys.wash("programs", query),
    enabled,
    placeholderData: keepPreviousData,
    queryFn: async () => {
      const items = [];
      let cursor: string | undefined;
      for (let page = 0; page < 10; page += 1) {
        const data = (await api.GET("/wash/programs", { params: { query: { ...query, cursor } as never } }))
          .data!;
        items.push(...data.items);
        if (!data.page.hasMore || !data.page.nextCursor) break;
        cursor = data.page.nextCursor;
      }
      return items;
    },
  });
}

/**
 * Program ürünü ve kimyasal seçenekleri: iş birimi WASH olan etkin ürünler + (iş birimi ne olursa olsun) etkin sarf
 * malzemeleri (`CONSUMABLE`). Tür / birim süzgeci çağıranda (`isProgramProductFor`).
 */
export function useWashProducts(enabled = true) {
  return useQuery({
    queryKey: queryKeys.wash("products"),
    enabled,
    staleTime: 300_000,
    queryFn: async () => {
      const [wash, consumables] = await Promise.all(
        [
          { businessUnit: "WASH", isActive: "true", limit: 200 },
          { productType: "CONSUMABLE", isActive: "true", limit: 200 },
        ].map(
          async (query) => (await api.GET("/products", { params: { query: query as never } })).data!.items,
        ),
      );
      const byId = new Map([...wash!, ...consumables!].map((product) => [product.id, product]));
      return [...byId.values()].sort((a, b) => a.code.localeCompare(b.code, "tr"));
    },
  });
}

/**
 * İstasyonun etkin su / elektrik sayaçları (ünite ayarındaki sayaç seçimi): `GET /wash/meters` — `wash.manage` yeterli,
 * gider modülü izni gerekmez. Ad, iş birimi ve üst sayaç adı gelir.
 */
export function useWashMeters(stationId: string | null, enabled = true) {
  return useQuery({
    queryKey: queryKeys.wash("meters", stationId),
    enabled: enabled && stationId !== null,
    staleTime: 120_000,
    queryFn: async () =>
      (await api.GET("/wash/meters", { params: { query: { stationId: stationId! } } })).data!.items,
  });
}

/** İstasyonun etkin personeli (iş emri / check-in personel seçimi): `GET /wash/staff` — `wash.operate` yeterli. */
export function useWashStaff(stationId: string | null, enabled = true) {
  return useQuery({
    queryKey: queryKeys.wash("staff", stationId),
    enabled: enabled && stationId !== null,
    staleTime: 120_000,
    queryFn: async () =>
      (await api.GET("/wash/staff", { params: { query: { stationId: stationId! } } })).data!.items,
  });
}

/**
 * Müşteri seçicisinin yıkama kaynağı (`GET /wash/customers`): en az 3 karakter, yanıt yalnızca kod + MASKELİ ad + kartın
 * son 4 hanesi (telefon yok). `cardText` kart ipucunu biçimler ("Kart …2345").
 */
export function washCustomerSource(
  stationId: string,
  cardText: (last4: string) => string,
): CustomerSearchSource {
  return {
    key: queryKeys.wash("customers", stationId),
    minLength: WASH_CUSTOMER_SEARCH_MIN_LENGTH,
    search: async (term) =>
      (await api.GET("/wash/customers", { params: { query: { stationId, q: term } } })).data!.items.map(
        (item) => ({
          id: item.id,
          code: item.code,
          name: item.name,
          masked: true,
          status: "ACTIVE" as const,
          detail: item.cardLast4 ? cardText(item.cardLast4) : null,
        }),
      ),
  };
}

export function useWashChemicals(stationId: string | null) {
  return useQuery({
    queryKey: queryKeys.wash("chemicals", stationId),
    enabled: stationId !== null,
    queryFn: async () =>
      (await api.GET("/wash/chemicals", { params: { query: { stationId: stationId! } } })).data!,
  });
}

// ── İşletim ──────────────────────────────────────────────────────────────────
export interface ActivationFilters {
  stationId: string;
  unitId?: string | undefined;
  status?: WashActivationStatus | undefined;
  source?: WashActivationSource | undefined;
}

export function activationsQuery(filters: ActivationFilters, cursor?: string, limit = 25) {
  return compact({ ...filters, cursor, limit });
}

export function useWashActivations(filters: ActivationFilters, cursor?: string) {
  const query = activationsQuery(filters, cursor);
  return useQuery({
    queryKey: queryKeys.wash("activations", query),
    placeholderData: keepPreviousData,
    queryFn: async () => (await api.GET("/wash/activations", { params: { query: query as never } })).data!,
  });
}

/** Tek aktivasyon: bekliyorken 3 sn'de bir yoklanır (olay kaçsa da canlı durum güncellenir). */
export function useWashActivation(id: string | null) {
  return useQuery({
    queryKey: queryKeys.wash("activation", id),
    enabled: id !== null,
    refetchInterval: (query) => (query.state.data?.status === "PENDING" ? 3000 : false),
    queryFn: async () => (await api.GET("/wash/activations/{id}", { params: { path: { id: id! } } })).data!,
  });
}

export interface CycleFilters {
  stationId: string;
  from?: string | undefined;
  to?: string | undefined;
  unitId?: string | undefined;
  trigger?: WashCycleTrigger | undefined;
  status?: WashCycleStatus | undefined;
}

export function cyclesQuery(filters: CycleFilters, cursor?: string, limit = 25) {
  return compact({ ...filters, cursor, limit });
}

export function useWashCycles(filters: CycleFilters, cursor?: string) {
  const query = cyclesQuery(filters, cursor);
  return useQuery({
    queryKey: queryKeys.wash("cycles", query),
    placeholderData: keepPreviousData,
    queryFn: async () => (await api.GET("/wash/cycles", { params: { query: query as never } })).data!,
  });
}

export interface CreditFilters {
  stationId?: string | undefined;
  status?: WashCreditStatus | undefined;
}

export function creditsQuery(filters: CreditFilters, cursor?: string, limit = 25) {
  return compact({ ...filters, cursor, limit });
}

export function useWashCredits(filters: CreditFilters, cursor?: string) {
  const query = creditsQuery(filters, cursor);
  return useQuery({
    queryKey: queryKeys.wash("credits", query),
    placeholderData: keepPreviousData,
    queryFn: async () => (await api.GET("/wash/credits", { params: { query: query as never } })).data!,
  });
}

export function useWashCredit(id: string) {
  return useQuery({
    queryKey: queryKeys.wash("credit", id),
    queryFn: async () => (await api.GET("/wash/credits/{id}", { params: { path: { id } } })).data!,
  });
}

export function useWashDayClosings(range: { stationId: string | null; from: string; to: string }) {
  const query = compact(range);
  return useQuery({
    queryKey: queryKeys.wash("day-closings", query),
    enabled: range.stationId !== null,
    placeholderData: keepPreviousData,
    queryFn: async () =>
      (await api.GET("/wash/day-closings", { params: { query: query as never } })).data!.items,
  });
}

// ── Raporlar ─────────────────────────────────────────────────────────────────
export function useWashSummary(range: {
  stationId: string | null;
  from: string;
  to: string;
  groupBy: "unit" | "program";
}) {
  const query = compact(range);
  return useQuery({
    queryKey: queryKeys.wash("summary", query),
    enabled: range.stationId !== null,
    placeholderData: keepPreviousData,
    queryFn: async () =>
      (await api.GET("/wash/reports/summary", { params: { query: query as never } })).data!,
  });
}

export function useWashOccupancy(range: {
  stationId: string | null;
  from: string;
  to: string;
  unitId?: string | undefined;
}) {
  const query = compact(range);
  return useQuery({
    queryKey: queryKeys.wash("occupancy", query),
    enabled: range.stationId !== null,
    placeholderData: keepPreviousData,
    queryFn: async () =>
      (await api.GET("/wash/reports/occupancy", { params: { query: query as never } })).data!,
  });
}

export function useWashFaults(range: {
  stationId: string | null;
  from: string;
  to: string;
  unitId?: string | undefined;
}) {
  const query = compact(range);
  return useQuery({
    queryKey: queryKeys.wash("faults", query),
    enabled: range.stationId !== null,
    placeholderData: keepPreviousData,
    queryFn: async () => (await api.GET("/wash/reports/faults", { params: { query: query as never } })).data!,
  });
}

// ── El yıkama ────────────────────────────────────────────────────────────────
export function useWashAppointments(filters: {
  stationId: string | null;
  from: string;
  to: string;
  unitId?: string | undefined;
  status?: WashAppointmentStatus | undefined;
}) {
  const query = compact({ ...filters, limit: 200 });
  return useQuery({
    queryKey: queryKeys.wash("appointments", query),
    enabled: filters.stationId !== null,
    placeholderData: keepPreviousData,
    queryFn: async () =>
      (await api.GET("/wash/appointments", { params: { query: query as never } })).data!.items,
  });
}

export function useWashAvailability(unitId: string | null, date: string, programIds: readonly string[]) {
  return useQuery({
    queryKey: queryKeys.wash("availability", { unitId, date, programIds }),
    enabled: unitId !== null && programIds.length > 0,
    placeholderData: keepPreviousData,
    queryFn: async () =>
      (
        await api.GET("/wash/units/{unitId}/availability", {
          params: { path: { unitId: unitId! }, query: { date, programIds: programIds.join(",") } },
        })
      ).data!,
  });
}

export interface WorkOrderFilters {
  stationId: string;
  status?: WashWorkOrderStatus | undefined;
  unitId?: string | undefined;
  /** Açılış günü (İstanbul). */
  from?: string | undefined;
  to?: string | undefined;
  /** Teslim günü (İstanbul) — "bugün teslim edilen" sütunu. */
  deliveredFrom?: string | undefined;
  deliveredTo?: string | undefined;
}

export function useWashWorkOrders(filters: WorkOrderFilters | null, cursor?: string, limit = 100) {
  const query = filters ? compact({ ...filters, cursor, limit }) : null;
  return useQuery({
    queryKey: queryKeys.wash("work-orders", query),
    enabled: query !== null,
    placeholderData: keepPreviousData,
    queryFn: async () => (await api.GET("/wash/work-orders", { params: { query: query as never } })).data!,
  });
}

/**
 * İş emri ayrıntısı. Hazır SMS'inin sonucu geçişten SONRA ayrı transaction'la yazılır: sonuç gelene dek (READY +
 * NOT_SENT) 3 sn'de bir yoklanır — olay (`wash.work_order.ready_notified`) kaçsa da ekran güncellenir.
 */
export function useWashWorkOrder(id: string) {
  return useQuery({
    queryKey: queryKeys.wash("work-order", id),
    refetchInterval: (query) =>
      query.state.data?.status === "READY" && query.state.data.readySmsStatus === "NOT_SENT" ? 3000 : false,
    queryFn: async () => (await api.GET("/wash/work-orders/{id}", { params: { path: { id } } })).data!,
  });
}

/** Sıra ekranı: olayla tazelenir; ayrıca 30 sn'de bir (TV ekranı saatlerce açık kalır, oda aboneliği kopabilir). */
export function useWashQueue(stationId: string | null) {
  return useQuery({
    queryKey: queryKeys.wash("queue", stationId),
    enabled: stationId !== null,
    refetchInterval: 30_000,
    queryFn: async () =>
      (await api.GET("/wash/queue", { params: { query: { stationId: stationId! } } })).data!,
  });
}
